// Copyright 2019 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { NavigationInstruction, Next, PipelineStep, Redirect } from "aurelia-router";
import { RouteAuthorizationMode } from "catalogs/RouteConfigCatalog";
import { INotificationService, NotificationUrgency } from "services/Notification/INotificationService";
import { IServerStatusService } from "services/Server/IServerStatusService";
import { IAuthentication } from "./IAuthentication";
import { OpenIDUnauthorizedNavigationStrategy } from "./OpenIDUnauthorizedNavigationStrategy";

@autoinject
export class OpenIDAuthenticationStep implements PipelineStep {
    constructor(
        private readonly authentication: IAuthentication,
        private readonly serverStatusService: IServerStatusService,
        private readonly notificationService: INotificationService,
        private readonly unauthorizedNavigationStrategy: OpenIDUnauthorizedNavigationStrategy
    ) {}

    public async run(instruction: NavigationInstruction, next: Next): Promise<unknown> {
        if (
            instruction.config &&
            instruction.config.settings &&
            instruction.config.settings.auth === RouteAuthorizationMode.Unauthorized
        ) {
            // No authentication required
            return next();
        }

        // Make sure that the server query is done and the serve is online
        let serverOnlineStatus = this.serverStatusService.online;

        if (serverOnlineStatus.inProgress) {
            await this.serverStatusService.updateStatus();
            serverOnlineStatus = this.serverStatusService.online;
        }

        const user = await this.authentication.getUser();

        // Check authentication
        if (
            (!!user && !user.expired) ||
            (instruction.config.settings.auth === RouteAuthorizationMode.OnlineAuthorized &&
                serverOnlineStatus.notAvailable)
        ) {
            return next();
        } else if (serverOnlineStatus.available) {
            const redirectQuery = this.unauthorizedNavigationStrategy.getRedirectUriString();
            const redirect = new Redirect(redirectQuery);
            return next.cancel(redirect);
        } else {
            // Show a notification here?
            this.notificationService.registerNotification(
                "Offline Mode",
                `The ${instruction.config.title} workflow is not available in offline mode`,
                NotificationUrgency.Info
            );
            return next.cancel();
        }
    }
}
